import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BlogTable from "./BlogTable";
import DynamicSelect from "../../DynamicSelect";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageHeading from "../../PageHeading";
import ConfirmActionModal from "../../ConfirmDeleteModal";

const BlogList = () => {
  const { t, i18n } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [resetFlag, setResetFlag] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [blogIdsToDelete, setBlogIdsToDelete] = useState([]);

  const navigate = useNavigate();

  // Fetch blogs on component mount
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const response = await axios.get("https://api.muktihospital.com/api/blogs", {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        });
        const blogsData = Array.isArray(response.data) ? response.data : response.data.blogs || [];
        setBlogs(blogsData);
        setFilteredBlogs(blogsData);
      } catch (error) {
        console.error("Error fetching blogs:", error);
        toast.error("Failed to fetch blog list");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const resetSelection = () => {
    setResetFlag((prev) => !prev);
  };

  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    filterBlogs(term, selectedLanguage);
  };

  // Handle Language Change
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "bn", label: "Bangla" },
  ];

  const handleLanguageChange = (selectedOption) => {
    const lang = selectedOption.value;
    setSelectedLanguage(lang);
    i18n.changeLanguage(lang);
    filterBlogs(searchTerm, lang);
  };

  // Filter blogs based on search term and selected language
  const filterBlogs = (term, language) => {
    const filtered = blogs.filter((blog) => {
      const title = blog.translations[language]?.title?.toLowerCase() || "";
      const description = blog.translations[language]?.description?.toLowerCase() || "";
      return title.includes(term) || description.includes(term);
    });
    setFilteredBlogs(filtered);
  };

  // Open delete confirmation modal
  const openDeleteModal = (ids) => {
    setBlogIdsToDelete(Array.isArray(ids) ? ids : [ids]); // Ensure IDs are always an array
    setDeleteModalOpen(true);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setBlogIdsToDelete([]);
  };

  // Confirm deletion
  const handleConfirmDelete = async (ids) => {
    try {
      // Delete blogs from the API
      await Promise.all(
        ids.map(async (id) => {
          await axios.delete(`https://api.muktihospital.com/api/blogs/delete/${id}`, {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          });
        })
      );

      // Update state to remove deleted blogs
      setBlogs((prev) => prev.filter((blog) => !ids.includes(blog.id)));
      setFilteredBlogs((prev) => prev.filter((blog) => !ids.includes(blog.id)));
      toast.success("Blog(s) deleted successfully!");
      resetSelection(); // Reset selection in BlogTable
    } catch (error) {
      console.error("Error deleting blogs:", error);
      toast.error(error.response?.data?.message || "Failed to delete blog(s).");
    }
  };

  // Handle Edit
  const handleEdit = (id) => {
    navigate(`/edit-blog/${id}`);
  };

  // Handle Add Blog
  const handleAdd = () => {
    navigate("/add-blog");
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/dashboard" },
    { label: "Manage Blogs"},
    { label: "All Blogs" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" />
      {/* Delete Confirmation Modal */}
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        ids={blogIdsToDelete}
        entityName="blog"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
      />
      <PageHeading title="All Blogs" breadcrumbs={breadcrumbs} />

      {/* Language Select and Search Input */}
      <div className="flex justify-between items-center mb-4">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((option) => option.value === selectedLanguage)}
          isSearchable
        />
        <input
          type="text"
          className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
          placeholder="Search by title or description"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      {/* Blog Table */}
      <BlogTable
        data={filteredBlogs}
        searchTerm={searchTerm}
        itemsPerPage={10}
        handleEdit={handleEdit}
        handleAdd={handleAdd}
        openDeleteModal={openDeleteModal}
        resetFlag={resetFlag}
        selectedLanguage={selectedLanguage}
      />
    </div>
  );
};

export default BlogList;